import { useMemo } from 'react'

import { useAuth } from '@/auth/use-auth'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * The permission codes the savings screens gate on.
 *
 * Every one is taken from the generated catalogue in `src/lib/permissions.ts` — none is written
 * out as a string here, because a code invented on this side would make a divergence from the
 * server invisible, which is precisely what regenerating that file exists to expose.
 *
 * Two of these are **not** savings codes, and that is the point of listing them. Every cash-moving
 * savings endpoint — deposit, withdrawal, security-deposit top-up, and the refund a closure pays
 * out — moves the money through the caller's own till, and on a counter till the drawer
 * additionally demands `teller.deposit.post` for cash in and `teller.withdrawal.post` for cash out.
 * Someone holding `savings.deposit.post` and nothing from Tellering gets a 403 from the drawer
 * after the customer has handed over the notes. Transfers are the one exception: they pass no till
 * type and touch no drawer.
 */
export const SAVINGS_PERMISSIONS = {
  view: PERMISSIONS.savings.view,
  statementView: PERMISSIONS.savings.statementView,
  accountOpen: PERMISSIONS.savings.accountOpen,
  depositPost: PERMISSIONS.savings.depositPost,
  withdrawalPost: PERMISSIONS.savings.withdrawalPost,
  withdrawalPostApprove: PERMISSIONS.savings.withdrawalPostApprove,
  transferPost: PERMISSIONS.savings.transferPost,
  securityDepositTopUp: PERMISSIONS.savings.securityDepositTopUp,
  holdPlace: PERMISSIONS.savings.holdPlace,
  holdRelease: PERMISSIONS.savings.holdRelease,
  close: PERMISSIONS.savings.close,
  closeApprove: PERMISSIONS.savings.closeApprove,
  dormancyManage: PERMISSIONS.savings.dormancyManage,
  interestPost: PERMISSIONS.savings.interestPost,
  productView: PERMISSIONS.savings.productView,
  productManage: PERMISSIONS.savings.productManage,
  productManageApprove: PERMISSIONS.savings.productManageApprove,
  /** The till's own permission for cash in. Checked by the cash drawer, not by Savings. */
  tellerDepositPost: PERMISSIONS.teller.depositPost,
  /** The till's own permission for cash out. Checked by the cash drawer, not by Savings. */
  tellerWithdrawalPost: PERMISSIONS.teller.withdrawalPost,
} as const

export interface SavingsPermissions {
  canView: boolean
  canViewStatement: boolean
  canOpenAccount: boolean
  canPostDeposit: boolean
  canPostWithdrawal: boolean
  canCountersignWithdrawal: boolean
  canPostTransfer: boolean
  canTopUpSecurityDeposit: boolean
  canPlaceHold: boolean
  canReleaseHold: boolean
  canClose: boolean
  canCountersignClose: boolean
  canManageDormancy: boolean
  canPostInterest: boolean
  canViewProducts: boolean
  canManageProducts: boolean
  canCountersignProduct: boolean
  /** Holds the till's cash-in permission. Without it a deposit is refused by the drawer. */
  canMoveCashIn: boolean
  /** Holds the till's cash-out permission. Without it a payout is refused by the drawer. */
  canMoveCashOut: boolean
  /** True when any of the four cash-moving forms is worth offering at all. */
  canPostAnything: boolean
}

/**
 * What this user may do in savings. Presentation only — the server authorises every command
 * against the live catalogue, so a visible button is not an open door and a hidden one is not a
 * closed one.
 */
export function useSavingsPermissions(): SavingsPermissions {
  const { hasPermission } = useAuth()

  return useMemo(() => {
    const canPostDeposit = hasPermission(SAVINGS_PERMISSIONS.depositPost)
    const canPostWithdrawal = hasPermission(SAVINGS_PERMISSIONS.withdrawalPost)
    const canPostTransfer = hasPermission(SAVINGS_PERMISSIONS.transferPost)
    const canTopUpSecurityDeposit = hasPermission(SAVINGS_PERMISSIONS.securityDepositTopUp)

    return {
      canView: hasPermission(SAVINGS_PERMISSIONS.view),
      canViewStatement: hasPermission(SAVINGS_PERMISSIONS.statementView),
      canOpenAccount: hasPermission(SAVINGS_PERMISSIONS.accountOpen),
      canPostDeposit,
      canPostWithdrawal,
      canCountersignWithdrawal: hasPermission(SAVINGS_PERMISSIONS.withdrawalPostApprove),
      canPostTransfer,
      canTopUpSecurityDeposit,
      canPlaceHold: hasPermission(SAVINGS_PERMISSIONS.holdPlace),
      canReleaseHold: hasPermission(SAVINGS_PERMISSIONS.holdRelease),
      canClose: hasPermission(SAVINGS_PERMISSIONS.close),
      canCountersignClose: hasPermission(SAVINGS_PERMISSIONS.closeApprove),
      canManageDormancy: hasPermission(SAVINGS_PERMISSIONS.dormancyManage),
      canPostInterest: hasPermission(SAVINGS_PERMISSIONS.interestPost),
      canViewProducts: hasPermission(SAVINGS_PERMISSIONS.productView),
      canManageProducts: hasPermission(SAVINGS_PERMISSIONS.productManage),
      canCountersignProduct: hasPermission(SAVINGS_PERMISSIONS.productManageApprove),
      canMoveCashIn: hasPermission(SAVINGS_PERMISSIONS.tellerDepositPost),
      canMoveCashOut: hasPermission(SAVINGS_PERMISSIONS.tellerWithdrawalPost),
      canPostAnything:
        canPostDeposit || canPostWithdrawal || canPostTransfer || canTopUpSecurityDeposit,
    }
  }, [hasPermission])
}
